import React from 'react';

const Chapter3Content = () => {
  return (
    <article className="prose prose-lg max-w-none">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Chapter Three</h1>
        <p className="text-xl text-gray-600">The Prerequisites</p>
        <p className="text-lg text-gray-500 mt-2">The Blueprints of His Coming — What God Commanded Us to Rehearse</p>
      </div>

      <div className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">The Fall Feasts: The Blueprints of His Return</h2>
        <p className="text-gray-800 leading-relaxed mb-4">
          Have you ever been in a school play?
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Before opening night, there are rehearsals. The director calls you in weeks — sometimes months — before the real performance. You practice your lines until they are automatic. You practice where to stand, when to enter. You run the whole play from beginning to end, again and again. The rehearsal is not the real thing. But it shows you exactly what the real thing will look like.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          God did something very much like that.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Long before Jesus walked the earth, God gave His people a set of appointed times — holy days He called His <em>moedim</em>, His divine appointments, His Rehearsals.
        </p>
      </div>

      {/* Spring Feasts Table */}
      <div className="mb-12">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">The Spring Feasts: Already Fulfilled</h3>
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-green-100">
                  <th className="p-3 text-left font-bold">Feast</th>
                  <th className="p-3 text-left font-bold">What It Rehearsed</th>
                  <th className="p-3 text-left font-bold">The Real Performance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-green-200">
                  <td className="p-3 font-semibold">Passover</td>
                  <td className="p-3">A spotless lamb dies to redeem God's people</td>
                  <td className="p-3">Jesus died on Passover — the Lamb of God</td>
                </tr>
                <tr className="border-t border-green-200">
                  <td className="p-3 font-semibold">Unleavened Bread</td>
                  <td className="p-3">A sinless life buried, untouched by corruption</td>
                  <td className="p-3">Jesus lay in the tomb, sinless</td>
                </tr>
                <tr className="border-t border-green-200">
                  <td className="p-3 font-semibold">Firstfruits</td>
                  <td className="p-3">The first sheaf of the harvest raised up</td>
                  <td className="p-3">Jesus rose — the firstfruit of all resurrection</td>
                </tr>
                <tr className="border-t border-green-200">
                  <td className="p-3 font-semibold">Pentecost</td>
                  <td className="p-3">The harvest comes in</td>
                  <td className="p-3">The Holy Spirit came; 3,000 gathered in one day</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-green-900 font-bold mt-4 text-center">
            Not one of these was a day early or a day late. God had been rehearsing them for centuries. When the moment came, He performed exactly on cue.
          </p>
        </div>
      </div>

      {/* Fall Feast One - Trumpets */}
      <div className="mb-16">
        <div className="bg-amber-50 border-l-4 border-amber-600 p-6 mb-6">
          <h3 className="text-2xl font-bold text-amber-900 mb-2">Fall Feast One — The Day of Trumpets</h3>
          <p className="text-amber-800 italic">Leviticus 23:24</p>
        </div>
        
        <p className="text-gray-800 leading-relaxed mb-4">
          "In the seventh month, on the first day of the month, you shall observe a day of solemn rest, a memorial proclaimed with blast of trumpets."
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          The season of awakening begins. Warning blasts fill the air. It is boot camp — a time of repentance, reckoning, and realignment.
        </p>

        {/* GIT Table for Day of Trumpets */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6 my-8">
          <h4 className="text-xl font-bold text-center text-amber-900 mb-4">GRAMMAR INTEGRITY TEST (G.I.T.)</h4>
          <h5 className="text-center font-bold mb-4">Fall Feast One — Day of Trumpets</h5>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-amber-100">
                  <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                  <th className="p-3 text-left font-bold">WHAT THE TEXT SAYS</th>
                  <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold align-top">SUBJECT<br/>(Who is this about?)</td>
                  <td className="p-3 align-top">God's covenant people — those commanded to observe and respond to the trumpet blasts</td>
                  <td className="p-3 align-top">The feast teaches the Church to be alert, listening, ready. Not absent — present and watching.</td>
                </tr>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold align-top">VERB<br/>(What does it do?)</td>
                  <td className="p-3 align-top">"Observe... proclaimed with blast of trumpets" — repeated blowing throughout the day</td>
                  <td className="p-3 align-top">The action is awakening. Each year rehearses: a great trumpet season is coming.</td>
                </tr>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold align-top">TIMELINE<br/>(When?)</td>
                  <td className="p-3 align-top">"First day of the seventh month" — the beginning of the fall season, not the climax</td>
                  <td className="p-3 align-top">The Day of Trumpets is not the last trumpet — it is the opening of the trumpet season.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
            <p className="text-center font-bold text-amber-900">✦ VERDICT ✦</p>
            <p className="text-center text-gray-800 mt-2">
              The Day of Trumpets teaches vigilance. The first blast is not the rapture — it is the call to awakening. The season is underway. Stay in the battle.
            </p>
          </div>
        </div>
      </div>

      {/* Fall Feast Two - Atonement */}
      <div className="mb-16">
        <div className="bg-amber-50 border-l-4 border-amber-600 p-6 mb-6">
          <h3 className="text-2xl font-bold text-amber-900 mb-2">Fall Feast Two — The Day of Atonement and the Tekiah Gedolah</h3>
          <p className="text-amber-800 italic">Leviticus 23:27–28, 25:9</p>
        </div>
        
        <p className="text-gray-800 leading-relaxed mb-4">
          After a full season of trumpet blasts, one final trumpet sounds — one long, continuous, world-stopping blast called the <strong>Tekiah Gedolah</strong>. The great blast. The final blast.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          Does that sound familiar? <em>"At the last trump... the dead will be raised incorruptible, and we shall be changed... O death, where is your sting?"</em>
        </p>

        {/* GIT Table for Atonement */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6 my-8">
          <h4 className="text-xl font-bold text-center text-amber-900 mb-4">GRAMMAR INTEGRITY TEST (G.I.T.)</h4>
          <h5 className="text-center font-bold mb-4">Fall Feast Two — Day of Atonement / Tekiah Gedolah</h5>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-amber-100">
                  <th className="p-3 text-left font-bold">GRAMMAR ELEMENT</th>
                  <th className="p-3 text-left font-bold">WHAT THE TEXT SAYS</th>
                  <th className="p-3 text-left font-bold">WHAT THIS TELLS US</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold align-top">SUBJECT</td>
                  <td className="p-3 align-top">The High Priest and His people — Jesus Christ as the Great High Priest</td>
                  <td className="p-3 align-top">In the last days fulfillment, Jesus acts; His people are transformed.</td>
                </tr>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold align-top">VERB</td>
                  <td className="p-3 align-top">"Sound a loud trumpet" — the Tekiah Gedolah. One final, unbroken blast.</td>
                  <td className="p-3 align-top">This is the last trumpet. It announces judgment is sealed, liberty proclaimed, dead are raised.</td>
                </tr>
                <tr className="border-t border-amber-200">
                  <td className="p-3 font-semibold align-top">TIMELINE</td>
                  <td className="p-3 align-top">"Tenth day of the seventh month" — after nine full days of trumpet blasts</td>
                  <td className="p-3 align-top">The last trumpet comes at the end of the season, not the beginning.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-6 bg-white border-2 border-amber-400 rounded-lg p-4">
            <p className="text-center font-bold text-amber-900">✦ VERDICT ✦</p>
            <p className="text-center text-gray-800 mt-2">
              The Day of Atonement is D-Day. The Tekiah Gedolah is the signal: death destroyed, captives freed, the dead raised. This is the last trumpet. It sounds at the end of the age.
            </p>
          </div>
        </div>
      </div>

      {/* Fall Feast Three - Tabernacles */}
      <div className="mb-16">
        <div className="bg-amber-50 border-l-4 border-amber-600 p-6 mb-6">
          <h3 className="text-2xl font-bold text-amber-900 mb-2">Fall Feast Three — The Feast of Tabernacles</h3>
          <p className="text-amber-800 italic">Leviticus 23:34, 42–43</p>
        </div>
        
        <p className="text-gray-800 leading-relaxed mb-4">
          Have you ever been to a wedding rehearsal? And then the rehearsal dinner after. That is what this feast is rehearsing — the <strong>Marriage Supper of the Lamb</strong>. God dwelling with His people.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-8">
          <h4 className="font-bold text-blue-900 mb-3">The Fixed Sequence</h4>
          <div className="space-y-2 text-blue-900">
            <p>📯 <strong>Day of Trumpets:</strong> Boot camp — learning the sound, staying alert</p>
            <p>⚔️ <strong>Day of Atonement:</strong> D-Day — Jesus fights, death destroyed, Tekiah Gedolah sounds</p>
            <p>🎉 <strong>Feast of Tabernacles:</strong> The Wedding Feast — dwelling with the King forever</p>
          </div>
        </div>

        <p className="text-gray-800 leading-relaxed mb-4">
          The fall feasts are not rehearsals for evacuation. They are boot camp, D-Day, and the wedding feast for the Kingdom Escort Guard.
        </p>
      </div>

      {/* Conclusion */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-8 text-center my-12">
        <p className="text-xl font-bold text-gray-900 mb-4">
          God fulfilled the spring feasts with surgical precision.
        </p>
        <p className="text-lg text-gray-800">
          Is there any reason to believe He will treat the fall feasts differently?
        </p>
      </div>

      <div className="flex justify-center gap-4 my-8">
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
      </div>
    </article>
  );
};

export default Chapter3Content;